import { useState, useEffect } from "react";
import { ToggleButton } from "@components/atoms/ToggleButton";
import {
  viewport,
  colors,
  font,
  lineHeight,
} from "../../../styles/cssinjs/helpers/variables.js";

function getSignupUrl(liveUrl, stagingUrl) {
  return process.env.NODE_ENV === "production" ? liveUrl : stagingUrl;
}

export default function StickyPlanComparision({
  categories,
  planCost,
  note,
  ctaButtons,
}) {
  const [showAll, setShowAll] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const getProLinkId = (categoryId) => {
    if (!categoryId) return "pricing-pro-table-annually";
    return `${categoryId}`;
  };

  const Heading = ({ title, plans, categoryId, ...props }) => {
    const Plans = () => (
      <>
        <li className="header-row" role="columnheader">
          <a
            id={getProLinkId(categoryId)}
            className="text-md-100 pricing-pro-table"
            href={getSignupUrl(
              "https://signup.setmore.com/signup?planId=5526",
              "https://signup.staging.setmore.com/signup?planId=5506"
            )}
          >
            Pro
            <svg width="20" height="20">
              <use href="/sprite.svg#setmore-emblem"></use>
            </svg>
          </a>
        </li>
        <li className="header-row text-md-100" role="columnheader">
          <p className="text-md-100">Free</p>
        </li>
        <style jsx>
          {`
            .header-row p,
            .header-row a {
              font-size: ${font.size.md[3]} !important;
              line-height: ${lineHeight[8]} !important;
              color: ${colors.primitive.green[800]};
              font-weight: ${font.weight.semiBold};
              font-family: ${font.style.primaryFont};
            }
            .header-row a {
              background-color: ${colors.primitive.grey[1800]};
              padding: 1px 15px;
              border-radius: 18px;
              transition: 0.4s box-shadow, 0.3s background-color; linear, 0.4s;
            }
            .header-row a:hover {
              background-color: ${colors.primitive.green[800]};
              color: ${colors.primitive.white.default};
            }
            .header-row a:hover > svg {
              fill: ${colors.primitive.white.default};
            }
            .plans {
              width: 49%;
              margin-left: 8%;
              border-radius: 8px;
            }
          `}
        </style>
      </>
    );
    return (
      <ul {...props} role="row">
        <span
          className="header-title text-smx fx fx--cv"
          role="cell"
          dangerouslySetInnerHTML={{ __html: title }}
        />
        <Plans />
        <style jsx>
          {`
            .header-title {
              min-width: 380px !important;
            }
          `}
        </style>
      </ul>
    );
  };

  const List = ({ data }) => {
    const FeatureAvailabilityList = ({
      heading,
      details,
      key,
      isTabed,
      isPro,
      isTooltip,
      istoggle,
      isSubscription,
      buttons = [],
      isLastRow,
    }) => (
      <ul
        className="ticked-bulletin list-item w-100 fx--cv feature-availability-list"
        role="row"
        key={key}
      >
        {heading ? (
          <div role="cell" className="text-xs feature-title fx fx--cv">
            {isPro ? (
              <svg width="40" height="20">
                <use xlinkHref="/sprite.svg#pro-badge"></use>
              </svg>
            ) : null}
            &nbsp; {heading}
            {isTooltip ? (
              <div className="tooltip-wrapper ml-1 mt-1">
                <span className="info-icon">
                  <svg width="24" height="24">
                    <use xlinkHref="/sprite.svg#info-icon"></use>
                  </svg>
                  <span
                    className="tooltip"
                    dangerouslySetInnerHTML={{ __html: isTooltip }}
                  ></span>
                </span>
              </div>
            ) : null}
          </div>
        ) : null}
        {istoggle && buttons.length === 2 ? (
          <>
            <ToggleButtonContainer plans={planCost.plans} />
            {buttons.map((btn) => {
              const href = getSignupUrl(btn.href.live, btn.href.staging);

              return (
                <div
                  key={btn.id}
                  role="cell"
                  className="feature-cell text-center sticky-toggle-wrap"
                >
                  <a id={btn.id} href={href} className={`btn ${btn.variant}`}>
                    {btn.content}
                  </a>
                </div>
              );
            })}
          </>
        ) : null}
        {!istoggle && !isSubscription
          ? details.map((item, index) => {
              if (item === "yes") {
                return (
                  <li
                    role="cell"
                    className="feature-cell text-center"
                    key={index}
                  >
                    <span className="sr-only">Yes</span>
                  </li>
                );
              } else if (item === "no") {
                return (
                  <li
                    role="cell"
                    className="feature-cell cross text-center"
                    key={index}
                  >
                    <span className="sr-only">No</span>
                  </li>
                );
              } else {
                if (!isSubscription) {
                  return (
                    <li
                      role="cell"
                      className="feature-cell text-center no-tick"
                      key={index}
                      dangerouslySetInnerHTML={{ __html: item }}
                    />
                  );
                }
              }
            })
          : null}

        {isSubscription ? (
          <>
            <li
              role="cell"
              className="feature-cell text-center no-tick cost-counter"
            >
              $<span className="cost monthly-cost">12</span>
              <span className="cost show annual-cost">5</span> user/month
            </li>
            <li role="cell" className="feature-cell text-center no-tick">
              $0 user/month
            </li>
          </>
        ) : null}
        <style jsx>
          {`
            .list-item {
              padding: 26px 20px !important;
              ${!isLastRow
                ? `border-bottom: 1px solid ${colors.primitive.grey[700]};`
                : ""}
              ${isTabed
                ? `border-left: 1px solid ${colors.primitive.grey[700]};
                border-bottom: 1px solid ${colors.primitive.grey[700]};`
                : ""}
              background-color: ${colors.primitive.grey[600]};
            }
            .cost {
              opacity: 0;
              visibility: hidden;
              position: absolute;
              transform: translateY(100%);
              transition: transform 0.5s ease, opacity 0.3s ease;
              top: -1px;
            }
            .cost.show {
              transform: translateY(0);
              opacity: 1;
              visibility: visible;
              position: static;
            }
            .cost-counter span {
              display: inline-block;
            }
            .cost-counter span:nth-of-type(1) {
              width: 18px;
            }
            .cost-counter span:nth-of-type(2) {
              width: 8px;
            }
            .feature-title {
              width: 92% !important;
              vertical-align: middle;
              color: ${colors.primitive.black[900]};
            }
            .feature-cell {
              font-size: 14px !important;
              width: 80% !important;
              margin-left: 3% !important;
              margin-right: 0px !important;
            }
            .feature-cell.no-tick {
              color: ${colors.primitive.grey[800]};
            }
            .no-tick::before {
              content: "";
              display: none;
            }
            .cross {
              position: relative;
            }
            .cross::before,
            .cross::after {
              content: "";
              position: absolute;
              left: 50%;
              top: 50%;
              width: 2px;
              height: 14px;
              background-color: #99a3ad;
              transform-origin: center;
            }
            .cross::before {
              transform: translate(-50%, -50%) rotate(45deg);
            }
            .cross::after {
              transform: translate(-50%, -50%) rotate(-45deg);
            }
          `}
        </style>
      </ul>
    );

    return data.map((item, index) => {
      return (
        <div key={index} className="table-rows">
          {item.tab ? (
            <div
              className="tab fx fx--ch"
              role="rowheader"
              dangerouslySetInnerHTML={{ __html: item.tab }}
            />
          ) : null}
          <div
            className={`table-rows w-100 ${!item.tab ? "mt-1" : ""}`}
            key={index}
          >
            {item.features?.map((feature, featureIndex) => {
              const isLastRow = featureIndex === item.features.length - 1;
              return (
                <FeatureAvailabilityList
                  heading={feature.heading}
                  details={feature.details}
                  isTabed={!!item.tab}
                  isPro={feature.proOnly}
                  isTooltip={feature.istooltip}
                  istoggle={feature.istoggle}
                  isSubscription={feature.isSubscription}
                  buttons={feature.buttons}
                  isLastRow={isLastRow}
                  key={`${index}-${featureIndex}`}
                />
              );
            })}
          </div>
          <style jsx>{`
            .table-rows {
              padding: 0px;
            }
            .tab {
              padding: 6px 20px;
              margin-top: 24px;
              max-width: fit-content;
              background-color: ${colors.primitive.grey[700]};
              color: ${colors.primitive.grey[800]};
              opacity: 0.75;
              text-transform: uppercase;
              font-size: ${font.size.sm[2]};
              line-height: ${lineHeight[5]};
              letter-spacing: 0.05em;
              font-weight: ${font.weight.bold};
              border-radius: 8px 8px 8px 0px !important;
            }
            .table-section:hover,
            .table-rows:hover,
            .tab:hover {
              box-shadow: none !important;
            }
            .ticked-bulletin p {
              text-align: right;
            }
          `}</style>
        </div>
      );
    });
  };

  const Category = ({ title, features, index, plans, isOpen, id }) => {
    return (
      <section
        id={id}
        className={`table-section p-0 w-100 bg-light-blue-grey ${
          index > 0 ? "lg:mt-8 mt-5" : isOpen ? "md:mt-5" : ""
        }`}
      >
        <div className="table-content w-100">
          <div className="table-header">
            <Heading
              title={title}
              plans={plans}
              categoryId={id}
              className={`p-block`}
            />
          </div>
          <div className={`transition-wrapper`}>
            <List data={features} key={index} />
          </div>
        </div>
      </section>
    );
  };

  const ToggleButtonContainer = ({ plans }) => (
    <div
      className="billing-type-wrap boxshadow-none w-100 lg:mr-auto"
      role="cell"
    >
      {plans ? (
        <span
          id="monthly-billing-span-cta "
          className="w-auto monthly-billing-span"
        >
          {plans[0]}
        </span>
      ) : null}
      <div className="togg-btn-wrap">
        <ToggleButton
          label={`click to toggle between monthly billing and annual billing.`}
          announcement={{
            onState:
              "annual billing - free plan $0 per month, pro plan $5 per month, team $5 per month.",
            offState:
              "monthly billing - free plan $0 per month, pro plan $12 per month, team $9 per month.",
          }}
          toggled={true}
        />
      </div>
      {plans ? (
        <span
          id="annual-billing-span-cta"
          className="annual-billing-span active"
          aria-hidden="true"
        >
          {plans[1]}
        </span>
      ) : null}
      <figure className="abs--img monthly-billing-span" id="dis-img-toggle-cta">
        <svg width="106" height="24">
          <use href="/sprite.svg#fifty-annual"></use>
        </svg>
      </figure>
      <style jsx>{`
        .billing-type-wrap {
          position: relative;
        }
        .billing-type-wrap span {
          vertical-align: middle;
          display: inline-block;
          font-size: ${font.size.sm[4]};
        }
        .annual-billing-span.active,
        .monthly-billing-span.active {
          font-weight: ${font.weight.bold};
          color: ${colors.primitive.black[100]};
        }
        .billing-type-wrap span:first-child {
          text-align: right;
        }
        .togg-btn-wrap {
          display: inline-block;
          vertical-align: middle;
          margin: 0 12px 0 0;
        }
        #dis-img-toggle-cta {
          left: 43px;
          position: absolute;
          top: -22px;
          display: inline-block;
          opacity: 0;
        }
        #dis-img-toggle-cta.abs--img {
          opacity: 0;
        }
        #dis-img-toggle-cta.active {
          opacity: 1;
        }
        .abs--img {
          top: 90px;
          left: 84px;
          position: absolute;
          transition-property: opacity;
          transition-duration: 0.5s;
        }
        #dis-img-toggle {
          position: relative;
          left: -30%;
          top: -5px;
          display: inline-block;
          opacity: 0;
        }
        #annual-dis-img-cta.abs--img,
        #annual-dis-img.abs--img,
        #annual-pro-dis-img.abs--img,
        #dis-img-toggle.abs--img,
        #monthly-dis-img-cta.abs--img,
        #monthly-dis-img.abs--img {
          opacity: 0;
        }
        #annual-dis-img-cta.active,
        #annual-dis-img.active,
        #annual-pro-dis-img.active,
        #dis-img-toggle.active,
        #monthly-dis-img-cta.active,
        #monthly-dis-img.active {
          opacity: 1;
        }
        .billing-toggle {
          margin-left: 38%;
          margin-right: -25px;
        }
        @media (max-width: ${viewport.tablet.sm}) {
          .abs--img {
            left: 66px;
          }
          #dis-img-toggle {
            left: 20%;
          }
          .billing-toggle #dis-img-toggle {
            left: -30%;
            top: -5px;
          }
        }
      `}</style>
    </div>
  );

  useEffect(() => {
    const checkViewport = () => setIsMobile(window.innerWidth <= 576);
    checkViewport();
    window.addEventListener("resize", checkViewport);
    return () => window.removeEventListener("resize", checkViewport);
  }, []);

  useEffect(() => {
    if (showAll || !isMobile) {
      const pricingWrapper = document.querySelector(".pricing-container");
      if (pricingWrapper) {
        const pricingType = pricingWrapper.id;
        const disImgToggleCta = document.querySelector("#dis-img-toggle-cta");
        if (disImgToggleCta) {
          if (pricingType === "monthly-billing-plan") {
            disImgToggleCta.classList.add("active");
          } else {
            disImgToggleCta.classList.remove("active");
          }
        }
      }
    }
  }, [showAll, isMobile]);
  return (
    <section className="pricing-wrap-container" id="pricing-wrap-container">
      <div className="pricing-wrap container pr-features lg:mt-10">
        <div
          role="table"
          className={`card--default has-three-col has-gutter-md mx-auto lg:-mt-10 transition-wrapper ${
            showAll || !isMobile ? "open" : "closed"
          }`}
        >
          {categories?.map((category, index) => (
            <Category
              title={category.title}
              features={category.list}
              plans={category.plans ?? ""}
              key={index}
              index={index}
              isOpen={showAll}
              id={category.id}
            />
          ))}
        </div>

        {note && (
          <p
            className="lg:mt-5 md:mt-2 lg:px-2 container text-left text-grey-800 text-xxs"
            dangerouslySetInnerHTML={{ __html: note }}
          />
        )}
      </div>

      {isMobile ? (
        <div className="container mt-5">
          {showAll && ctaButtons && ctaButtons.buttons.length === 2 ? (
            <>
              {ctaButtons.buttons.map((btn) => {
                const href = getSignupUrl(btn.href.live, btn.href.staging);

                return (
                  <div
                    key={btn.id}
                    role="cell"
                    className="feature-cell text-center sticky-toggle-wrap"
                  >
                    <a
                      id={btn.id}
                      href={href}
                      className={`btn w-100 ${btn.variant}`}
                    >
                      {btn.content}
                    </a>
                  </div>
                );
              })}
            </>
          ) : null}
          <button
            onClick={() => {
              setShowAll(!showAll);
              if (showAll) {
                setTimeout(() => {
                  document
                    .getElementById("pricing-wrap-container")
                    ?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                      inline: "start",
                    });
                }, 0);
              }
            }}
            id="toggle-btn"
            className="mt-5 accordion-btn"
          >
            {showAll ? (
              <>
                Hide all features
                <svg width="34" height="35" viewBox="0 0 34 35" fill="none">
                  <path
                    d="M13.34 12.6c-.39-.39-1.02-.39-1.41 0-.39.39-.39 1.02 0 1.41L15.59 17.67l-3.69 3.69c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0L17 19.09l3.69 3.69c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41l-3.69-3.69 3.66-3.66c.39-.39.39-1.02 0-1.41-.39-.39-1.02-.39-1.41 0L17 16.26l-3.66-3.66z"
                    fill="#48A36C"
                  />
                </svg>
              </>
            ) : (
              <>
                <span>Explore all features</span>
                <svg width="24" height="25" viewBox="0 0 24 25" fill="none">
                  <path
                    d="M13.025 6.508a1 1 0 1 0-2 0v5.175H5.8a1 1 0 1 0 0 2h5.225v5.225a1 1 0 1 0 2 0v-5.225h5.175a1 1 0 1 0 0-2h-5.175V6.508z"
                    fill="#48A36C"
                  />
                </svg>
              </>
            )}
          </button>
        </div>
      ) : null}
    </section>
  );
}
